/**
 * Copyright 2020 (c) Dominik Basner, Sotec GmbH (for VDW e.V.)
 * Copyright 2020 (c) Fabian Wilhelm, Sotec GmbH (for VDW e.V.)
 * Copyright 2020-2021 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2023 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 * Copyright 2026 (c) Alexander Uhl, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import { labels } from '@umati/dashboard-common';
import { IMachine } from '@umati/dashboard-types';
import classNames from 'classnames';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { setMachineFavourite, setMachineFavouriteType } from 'redux/actions';
import FrontendURLs from 'utilities/FrontendURLs';
import MachineHandling from 'utilities/MachineHandling';
import TemplateHelper from 'utilities/TemplateHelper';
import StatusTimeline from '../timeline/StatusTimeline';

const activeMachineIds = new Set<string>();

const ConditionalWrapper = ({ condition, wrapperCond, wrapperNotCond, children }) =>
  condition ? wrapperCond(children) : wrapperNotCond(children);

function Location(props) {
  const machine = props.machine;
  const fairTag = props.fairTag;
  if (fairTag && machine.Positions?.[fairTag]) {
    return (
      <span>
        {machine.Positions?.[fairTag]?.hall} {machine.Positions?.[fairTag]?.booth}
      </span>
    );
  } else if (machine.Positions?.offsiteClean) {
    return (
      <span>
        {machine.Positions.offsiteClean.lat}, {machine.Positions.offsiteClean.lng}
      </span>
    );
  } else {
    return <span>{labels.noPosition}</span>;
  }
}

interface IMachineListElementProps {
  machine: IMachine;
  fairTag: string;
  hideOnline?: boolean;
  hideFav?: boolean;
  cb?: {
    expand: () => void;
  };
  in?: boolean;
}

interface IMachineListElementPropsEx {
  setMachineFavourite: setMachineFavouriteType;
}

interface IMachineListElementPropsFull extends IMachineListElementProps, IMachineListElementPropsEx {}

class MachineListElementContent extends Component<IMachineListElementPropsFull> {
  private mountedMachineId?: string;

  componentDidMount() {
    this.logInvalidMachineName();
  }

  componentDidUpdate(prevProps: IMachineListElementPropsFull) {
    if (prevProps.machine !== this.props.machine) {
      this.logInvalidMachineName();
    }
  }

  componentWillUnmount() {
    if (this.mountedMachineId) {
      activeMachineIds.delete(this.mountedMachineId);
    }
  }

  private logInvalidMachineName() {
    const machineName = MachineHandling.getMachineName(this.props.machine);
    if (machineName === undefined || machineName === null || machineName.trim() === '' || machineName === 'Undefined') {
      console.error('Invalid machineName for MachineListElement', {
        machineId: this.props.machine.MachineId,
        machineName,
        machine: this.props.machine,
      });
    }
  }

  handeExpand = (e) => {
    e.stopPropagation();
    this.props.cb?.expand();
  };

  handleLink = (e) => {
    e.stopPropagation();
  };

  render() {
    const encodingVersion = MachineHandling.getEncodingVersion(this.props.machine);
    /// \todo replace by machineName (this includes scroll logic in machineList)
    const model = MachineHandling.getMachineModel(this.props.machine);
    const id = model ? model.replace(/\s+/g, '') : '';
    const machineName = MachineHandling.getMachineName(this.props.machine);
    const fairTag = this.props.fairTag;
    const linkTarget = FrontendURLs.getMachineWoEncoding(this.props.machine);
    const machine = this.props.machine;
    const template = TemplateHelper.getTemplateByTypeId(machine.TypeDefinition);

    const manufacturerDisplay = MachineHandling.getManufacturerName(this.props.machine);

    return (
      <li className="list-group-item">
        <ConditionalWrapper
          wrapperNotCond={(children) => (
            <Link to={linkTarget} className="list-group-item-link">
              {children}
            </Link>
          )}
          wrapperCond={(children) => (
            <div onClick={this.handeExpand} className="list-group-item-link">
              {children}
            </div>
          )}
          condition={this.props.cb}
        >
          <div className="row align-items-center" id={id}>
            <div className="col">
              {/* toggle "online" class to change status */}
              {!this.props.hideOnline && (
                <span
                  className={classNames('icon-connection', { online: this.props.machine.Online })}
                  title={labels.connectionInfo}
                />
              )}
              <h4>
                <object>
                  <Link onClick={(e) => e.stopPropagation()} to={`${linkTarget}?v=${encodingVersion}`}>
                    {machineName}
                  </Link>
                </object>
              </h4>
              <div className="row list-group-item-data">
                <div className="col-12 col-sm-4">
                  <b>{labels.manufacturer}</b> {manufacturerDisplay}
                </div>
                <div className="col-12 col-sm-4">
                  <b>{labels.location}</b>
                  <Location machine={machine} fairTag={fairTag} />
                </div>
                <div className="col-12 col-sm-4">
                  <b>Encoding</b> v{encodingVersion}
                </div>
              </div>
            </div>
            {!this.props.hideFav && (
              <div className="col-auto">
                {/* toggle "active" class to change status */}
                <button
                  className={classNames('btn-favorite', { active: machine.Favorite })}
                  value={0}
                  title={labels.markAsFavorite}
                  onClick={() => this.props.setMachineFavourite(machine.MachineId, !machine.Favorite)}
                />
              </div>
            )}
          </div>
        </ConditionalWrapper>
        {this.props.in && (
          <div className="list-group-item-timeline">
            <StatusTimeline
              namespaceUri={machine.MachineId}
              colorMap={template ? TemplateHelper.getTimelineColorMap(template.timeline) : undefined}
            />
          </div>
        )}
      </li>
    );
  }
}

class MachineListElement extends Component<IMachineListElementPropsFull, { hasError: boolean; error?: Error }> {
  constructor(props: IMachineListElementPropsFull) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('MachineListElement error boundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const machineId = this.props.machine?.MachineId;
      const error = this.state.error;
      const stackLines = error?.stack?.split('\n').slice(0, 4).join('\n');
      return (
        <li className="list-group-item list-group-item-danger">
          <div>
            <strong>MachineListElement Error Boundary Caught</strong>
          </div>
          <div>MachineId: {machineId}</div>
          {error && <div>Error: {error.message}</div>}
          {stackLines && (
            <pre style={{ fontSize: '0.75em', whiteSpace: 'pre-wrap', marginBottom: 0 }}>{stackLines}</pre>
          )}
          <div>Please check the data of the machine entry.</div>
        </li>
      );
    }

    return <MachineListElementContent {...this.props} />;
  }
}

export default connect(null, { setMachineFavourite })(MachineListElement);
